import { adminApi } from './api-client';

/**
 * Typed wrappers around the admin-api staff User Management routes
 * (Phase 5d-3a, ADR-039 — lambdas/admin-api/src/handlers.ts). The Lambda is
 * the authority for the response shapes; this module mirrors them.
 *
 * Every endpoint is super_admin-only server-side (ADR-012). The portal also
 * gates the /admin/users surface + its COG item to super_admin so the UI is
 * never shown for actions the caller cannot perform.
 */

export type StaffRole = 'super_admin' | 'nexa_admin' | 'support' | 'read_only' | 'audit_only';
/** 'invited' = created, pending first sign-in; flips to 'active' on sign-in. */
export type StaffStatus = 'active' | 'disabled' | 'invited';

/** Friendly role labels for display. Gate logic always uses the constants. */
export const STAFF_ROLE_LABELS: Record<StaffRole, string> = {
  super_admin: 'Super Admin',
  nexa_admin: 'Nexa Admin',
  support: 'Support',
  read_only: 'Read Only',
  audit_only: 'Auditor',
};

/** One-line role descriptions for the legend (ADR-012 tiers, plain language). */
export const STAFF_ROLE_DESCRIPTIONS: Record<StaffRole, string> = {
  super_admin: 'Full access, including staff management and security controls.',
  nexa_admin: 'Day-to-day operations — customers, domains, partner decisions.',
  support: 'Customer-recovery operations (e.g. reactivate a subscription) + PII reads.',
  read_only: 'Audit observability — can view, cannot change anything.',
  // ADR-015 — audit_only is the renamed read_only tier; treated identically
  // for all gates (5d-3b UAT Fix 1). Surfaced as "Auditor" going forward.
  audit_only: 'Read-only access to all views; no mutations.',
};

export const STAFF_ROLES: StaffRole[] = ['super_admin', 'nexa_admin', 'support', 'read_only', 'audit_only'];

export function staffRoleLabel(role: string): string {
  return (STAFF_ROLE_LABELS as Record<string, string>)[role] ?? role;
}

export interface StaffListItem {
  cognitoSub: string;
  staffId: string;
  role: string;
  email: string;
  displayName?: string;
  staffStatus: StaffStatus;
  createdAt?: string;
  uatTest?: boolean;
  geoOverrideUntil?: string;
  /** Live Cognito sign-in state (#7) — CONFIRMED vs FORCE_CHANGE_PASSWORD. The
   *  list badge reads this so a password reset shows "Pending sign-in", not the
   *  stale "Active" the DDB staffStatus would imply. Best-effort enrich. */
  cognitoStatus?: string;
  /** True when a CONFIRMED staffer has no software-token MFA enrolled (#7) —
   *  i.e. their authenticator was reset and they must re-enrol on next sign-in. */
  mfaPending?: boolean;
}

export interface StaffSignInEntry {
  at: string;
  country?: string;
  newDeviceUsed: boolean;
}

export interface StaffDetail extends StaffListItem {
  roleLabel: string;
  createdBy?: string;
  disabledAt?: string;
  disabledBy?: string;
  /** Cognito sign-in state — CONFIRMED (active) vs FORCE_CHANGE_PASSWORD
   *  (pending first sign-in). Best-effort enrich; may be undefined. */
  cognitoStatus?: string;
  cognitoEnabled?: boolean;
  /** True when a CONFIRMED staffer has no software-token MFA enrolled (#7) —
   *  authenticator was reset; re-enrol forced on next sign-in. */
  mfaPending?: boolean;
}

export interface StaffAuditEntry {
  action: string;
  performedBy: string;
  performedByStaffId: string;
  at: string;
  detail?: string;
}

/** Human labels for the audit action codes the Lambda writes. */
export const STAFF_AUDIT_ACTION_LABELS: Record<string, string> = {
  staff_invited: 'Invited',
  staff_reinvited: 'Re-invited',
  role_changed: 'Role changed',
  staff_disabled: 'Disabled',
  staff_enabled: 'Re-enabled',
  password_reset: 'Password reset',
  mfa_reset: 'Authenticator reset',
};

export function listStaff(): Promise<{ staff: StaffListItem[] }> {
  return adminApi<{ staff: StaffListItem[] }>('/admin/staff');
}

export function getStaff(cognitoSub: string): Promise<{ staff: StaffDetail }> {
  return adminApi<{ staff: StaffDetail }>(`/admin/staff/${encodeURIComponent(cognitoSub)}`);
}

export interface InviteStaffInput {
  email: string;
  role: StaffRole;
  name?: string;
  /** UAT test mode — fakes the email send, auto-confirms with a known
   *  password (returned in the response), tags the account `uatTest`. */
  uatTest?: boolean;
  /** ADR-124 A1 — the new member's default working zone (zone code), set by the creating admin. */
  preferredZone?: string;
}
export interface InviteStaffResponse {
  cognitoSub: string;
  role: StaffRole;
  email: string;
  resent: boolean;
  uatTest: boolean;
  /** Live invites: whether the SES invite actually sent. false (with
   *  emailError) means the recipient is unverified in SES sandbox, etc. */
  emailSent: boolean;
  emailError?: string;
  /** Present only for test-mode invites — the known password to sign in with. */
  uatTestPassword?: string;
}
export function inviteStaff(input: InviteStaffInput): Promise<InviteStaffResponse> {
  return adminApi<InviteStaffResponse>('/admin/staff', {
    method: 'POST',
    body: JSON.stringify({
      email: input.email,
      role: input.role,
      ...(input.name ? { name: input.name } : {}),
      ...(input.uatTest ? { uatTest: true } : {}),
      ...(input.preferredZone ? { preferredZone: input.preferredZone } : {}),
    }),
  });
}

export function updateStaffRole(
  cognitoSub: string,
  role: StaffRole,
): Promise<{ staffSub: string; role: StaffRole; previousRole?: string; unchanged?: boolean }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify({ role }),
  });
}

/** Edit a staff member's display name (email/username are not editable —
 *  they're the Cognito sign-in alias). */
export function updateStaffName(
  cognitoSub: string,
  displayName: string,
): Promise<{ staffSub: string; displayName: string }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify({ displayName }),
  });
}

/**
 * PL7 (UAT iter-2) — one-Save on the staff user modal: persist display name
 * and/or role in a SINGLE PUT (the handler already accepts both together),
 * mirroring the subscriber updateSubscriber pattern. Send only the changed
 * fields; both branches hit PUT /admin/staff/{sub}.
 */
export function updateStaff(
  cognitoSub: string,
  input: { displayName?: string; role?: StaffRole },
): Promise<{ staffSub: string; role?: StaffRole; displayName?: string; previousRole?: string; unchanged?: boolean }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...(input.displayName !== undefined ? { displayName: input.displayName } : {}),
      ...(input.role !== undefined ? { role: input.role } : {}),
    }),
  });
}

export function disableStaff(
  cognitoSub: string,
  reason?: string,
): Promise<{ staffSub: string; staffStatus: StaffStatus }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}/disable`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export function enableStaff(
  cognitoSub: string,
): Promise<{ staffSub: string; staffStatus: StaffStatus }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}/enable`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function listStaffAudit(
  cognitoSub: string,
): Promise<{ audit: StaffAuditEntry[]; signIns: StaffSignInEntry[] }> {
  return adminApi<{ audit: StaffAuditEntry[]; signIns: StaffSignInEntry[] }>(
    `/admin/staff/${encodeURIComponent(cognitoSub)}/audit`,
  );
}

/**
 * Hard-delete (purge) — irreversibly removes the Cognito user + Staff record +
 * audit trail. super_admin + fresh-auth + typed-confirmation: `confirmEmail`
 * must match the member's email (echoed server-side). Distinct from disable
 * (soft, reversible). Primarily UAT cleanup + full removal of a departed
 * staffer.
 */
export function purgeStaff(
  cognitoSub: string,
  confirmEmail: string,
): Promise<{ staffSub: string; hardDeleted: boolean }> {
  return adminApi(`/admin/staff/${encodeURIComponent(cognitoSub)}/purge`, {
    method: 'DELETE',
    body: JSON.stringify({ confirm: confirmEmail }),
  });
}

/** Result of a staff password reset (item #7, Action A) — the temp password is
 *  EMAILED, never returned to the portal. `emailSent:false` (with `emailError`)
 *  means the recipient is unverified in SES sandbox, etc. */
export interface StaffPasswordResetResult {
  email: string;
  emailSent: boolean;
  emailError?: string;
}

/**
 * Reset a staff member's password (batch-2 Stage 2, item #7, Action A).
 * super_admin + fresh-auth, staff pool. EMAILS a fresh temporary password
 * (reusing the invite-email machinery) and sets FORCE_CHANGE_PASSWORD — the
 * staffer sets their own on first sign-in. Their authenticator (TOTP) is
 * UNTOUCHED. Nothing is shown on screen.
 */
export function resetStaffPassword(cognitoSub: string): Promise<StaffPasswordResetResult> {
  return adminApi<StaffPasswordResetResult>(
    `/admin/staff/${encodeURIComponent(cognitoSub)}/reset-password`,
    { method: 'POST' },
  );
}

/**
 * Reset a staff member's authenticator / TOTP enrolment (batch-2 Stage 2,
 * item #7, Action B). super_admin + fresh-auth, staff pool. Clears software-token
 * MFA so the pool (which mandates TOTP) forces re-enrolment on next sign-in —
 * lost-device recovery. The PASSWORD is untouched.
 *
 * ADR-074 D4 (Stage 4): the action now also sends a notification email (the new
 * observable `mfa_reset` flow). `emailSent` is surfaced — never swallowed behind
 * a bare `{ ok }` (frontend-behaviour-lessons #4) — so the toast can tell the
 * operator whether the notification actually went; a failed send is non-fatal
 * (the reset still stands).
 */
export function resetStaffAuthenticator(
  cognitoSub: string,
): Promise<{ ok: boolean; emailSent?: boolean; emailError?: string }> {
  return adminApi<{ ok: boolean; emailSent?: boolean; emailError?: string }>(
    `/admin/staff/${encodeURIComponent(cognitoSub)}/reset-authenticator`,
    { method: 'POST' },
  );
}
